import moment from 'moment';
import _ from 'underscore';
import SessionStore from 'stores/shared/session';
import { t, loadLocales } from 'services/translation';
import validate from 'validate.js';
import { isValidDate } from 'ui_system/utilities/timezoneUtils';

function titleize(fieldName) {
  return _.str.titleize(_.str.humanize(fieldName));
}

function meetsAgeRequirement(question) {
  const birthdateMomentObject = moment(question.answer, question.date_format);
  const birthdate = birthdateMomentObject.toDate();
  const allowedDate = new Date();
  const allowedYear = allowedDate.getFullYear() - question.minimum_value;
  allowedDate.setFullYear(allowedYear);
  if (birthdate > allowedDate) {
    return false;
  }
  return true;
}

export default {
  hasOneTrue(value, options, key, attributes) {
    const isTruthy = _.find(_.values(value), (v) => {
      return v === true;
    });

    if (isTruthy) {
      return null;
    }
    return t('AT_LEAST_ONE_OPTION_MUST_BE_SELECTED');
  },

  isChecked(value, options, key, attributes) {
    if (value) {
      return null;
    }
    return options.message || `${titleize(key)} ${t('SHOULD_BE_CHECKED')}`;
  },

  presenceArray(value, options, key, attributes) {
    const hasNonEmptyContent = _.find(_.values(value), (v) => {
      return !_.isEmpty(v);
    });
    if (hasNonEmptyContent) {
      return null;
    }
    return t('AT_LEAST_ONE_INPUT_MUST_BE_FILLED_IN');
  },

  matches(value, options, key, attributes) {
    let errorMsg = null;

    // No need to worry about fieldToMatch not being present
    if (value !== attributes[options.fieldToMatch]) {
      errorMsg = ` ${t('DOESNT_MATCH')} ${titleize(options.fieldToMatch)}`;
    }
    return errorMsg;
  },

  presenceIf(value, options, key, attributes) {
    let errorMsg = null;

    if (options.condition && options.condition(attributes)) {
      errorMsg = validate.validators.presence(value, options);
    }

    return errorMsg;
  },

  greaterThan(value, options, key, attributes) {
    let errorMsg = null;

    options.percentage = options.percentage || 1;
    if (value < attributes[options.attribute] * options.percentage) {
      errorMsg = ` ${t('MUST_BE_GREATER_THAN')} ${
        options.percentage !== 1 ? `${options.percentage * 100}% of ` : ''
      }${titleize(options.attribute)}`;
    }

    return errorMsg;
  },

  lessThan(value, options, key, attributes) {
    let errorMsg = null;

    options.percentage = options.percentage || 1;
    if (value > attributes[options.attribute] * options.percentage) {
      errorMsg =
        options.message ||
        ` ${t('CANNOT_EXCEED')} ${
          options.percentage !== 1 ? `${options.percentage * 100}% of ` : ''
        }${titleize(options.attribute)}`;
    }

    return errorMsg;
  },

  phonePresence(value, options, key, attributes) {
    let errorMsg = null;

    if ((value && !value[2]) || value === '') {
      errorMsg = ` ${t('CANNOT_BE_BLANK')}`;
    }

    return errorMsg;
  },

  checkoutQuestions(value, options, key, attributes) {
    const errors = {};
    let errorsPresent;
    loadLocales(SessionStore.state.get('i18n'));
    const minDateStringLength = 10; // includes the "/" characters
    for (const question of value) {
      if (
        question.required &&
        !(question.answer && String(question.answer).length)
      ) {
        errorsPresent = true;
        errors[question.position] = [`${t('PLEASE_SELECT_AN_OPTION')}.`];
      }
      // Handle a checkbox question with response required
      if (
        question.input_type === 'waiver' &&
        question.required &&
        (question.answer === undefined || question.answer === 'no')
      ) {
        errorsPresent = true;
        errors[question.position] = [`${t('RESPONSE_IS_REQUIRED')}.`];
      }
      if (question.input_type === 'text') {
        if (
          question.required &&
          !(question.answer && String(question.answer).length)
        ) {
          errorsPresent = true;
          errors[question.position] = [`${t('ANSWER_CANNOT_BE_BLANK')}.`];
        }
      }
      if (question.input_type === 'date') {
        if (
          (question.required || question.answer) &&
          !isValidDate(question.answer, question.date_format)
        ) {
          errorsPresent = true;
          errors[question.position] = [`${t('DATE_IS_INCOMPLETE')}.`];
        } else {
          // Only validate age requirement if valid date
          if (question.minimum_value) {
            if (!meetsAgeRequirement(question)) {
              errorsPresent = true;
              errors[question.position] = [
                `${t('SORRY_YOU_NEED_TO_BE_AT_LEAST_YEARS_OLD_TO_PROCEED', '', {
                  age: question.minimum_value,
                })}`,
              ];
            }
          }
        }
      }
    }

    if (errorsPresent) {
      return errors;
    }
    return null;
  },

  additionalGuests(value, options, key, attributes) {
    for (const guest of value) {
      if (!(guest.full_name && String(guest.full_name).length)) {
        return ` ${t('CANNOT_BE_BLANK')}`;
      }
    }
    return null;
  },
};
